import {invoiceAmounts} from '../invoice-posting-model';
/**
 * GST computation.
 *
 * Every figure that reaches the ledger or a return comes from here, so the
 * rules are stated explicitly rather than inferred at the call site.
 *
 * Order of operations, per section 15 read with rule 35:
 *   1. Line value        = quantity x unit price
 *   2. less line discount (percentage first, then flat amount)
 *   3. less apportioned document discount
 *   4. plus taxable charges apportioned across lines
 *   = taxable value, on which GST is computed
 *
 * Intra-state supply splits the rate equally into CGST and SGST; inter-state
 * charges the whole rate as IGST. Cess is additional and never split.
 */

import type { SalesCharge, SalesConfig, SalesLine, SupplyType, TaxTreatment } from "./types";

/** Rounds to two decimals using half-up, avoiding binary float drift. */
export const round2 = (value: number): number => {
  if (!Number.isFinite(value)) return 0;
  return Math.sign(value) * Math.round((Math.abs(value) + Number.EPSILON) * 100) / 100;
};

/** Treatments that carry no output tax. */
const NON_TAXABLE: TaxTreatment[] = ["zero-rated", "exempt", "nil-rated", "non-gst"];

export const attractsTax = (treatment: TaxTreatment): boolean =>
  !NON_TAXABLE.includes(treatment) && treatment !== "reverse-charge";

export interface LineTax {
  lineId: string;
  /** Value before any discount. */
  grossValue: number;
  discount: number;
  /** Share of the document-level discount allocated to this line. */
  documentDiscountShare: number;
  /** Share of taxable document charges allocated to this line. */
  chargeShare: number;
  taxableValue: number;
  gstRate: number;
  cgst: number;
  sgst: number;
  igst: number;
  cess: number;
  totalTax: number;
  lineTotal: number;
  treatment: TaxTreatment;
}

export interface DocumentTax {
  lines: LineTax[];
  /** Sum of line taxable values — the figure reported in GSTR-1. */
  taxableValue: number;
  /** Non-taxable charges, added after tax. */
  nonTaxableCharges: number;
  documentDiscount: number;
  cgst: number;
  sgst: number;
  igst: number;
  cess: number;
  totalTax: number;
  /** Taxable value plus tax plus non-taxable charges, before rounding. */
  grossTotal: number;
  roundOff: number;
  /** Amount payable by the customer. */
  total: number;
  /** TCS collected under 206C(1H) — added to the amount receivable. */
  tcs: number;
  /** TDS the customer will deduct — reduces cash received, not the invoice. */
  tdsWithheld: number;
  /** Cash expected after the customer's TDS deduction. */
  netReceivable: number;
  supplyType: SupplyType;
  rcm: boolean;
}

const lineGross = (line: SalesLine): number => line.taxInclusive&&line.taxTreatment==='taxable'?Number(invoiceAmounts([{quantity:String(line.quantity),rate:String(line.unitPrice),taxRate:String(line.gstRate),taxTreatment:'Regular',taxInclusive:true}]).subtotal):round2(line.quantity * line.unitPrice);

const lineDiscount = (line: SalesLine): number => {
  const gross = lineGross(line);
  const percentPart = line.discountPercent ? (gross * line.discountPercent) / 100 : 0;
  return round2(Math.min(gross, percentPart + (line.discountAmount ?? 0)));
};

/** Value net of line-level discount, before document-level adjustments. */
export const lineNetValue = (line: SalesLine): number => round2(lineGross(line) - lineDiscount(line));

export interface ComputeOptions {
 canonical?:boolean;adjustments?:import('../document-adjustments').DocumentAdjustments;
  supplyType: SupplyType;
  /** Reverse charge — supplier raises the invoice but charges no tax. */
  rcm?: boolean;
  roundOff?: boolean;
  /** TCS rate applied to the invoice total, when the threshold is crossed. */
  tcsRate?: number;
  /** TDS section rate the customer will apply to the taxable value. */
  tdsRate?: number;
}

/**
 * Apportions a document-level amount across lines in proportion to their net
 * value, forcing the last line to absorb the rounding remainder so the parts
 * always sum exactly to the whole. Without that correction a three-way split of
 * ₹100 leaves a stray paisa and the invoice fails its own balance check.
 */
const apportion = (total: number, weights: number[]): number[] => {
  const weightSum = weights.reduce((sum, weight) => sum + weight, 0);
  if (weightSum <= 0 || total === 0) return weights.map(() => 0);
  const shares = weights.map((weight) => round2((total * weight) / weightSum));
  const drift = round2(total - shares.reduce((sum, share) => sum + share, 0));
  if (drift !== 0) {
    const lastIndex = shares.length - 1;
    shares[lastIndex] = round2(shares[lastIndex] + drift);
  }
  return shares;
};

export const computeDocumentTax = (
  lines: SalesLine[],
  charges: SalesCharge[] = [],
  documentDiscount = 0,
  options: ComputeOptions
): DocumentTax => {
  const { supplyType, rcm = false, roundOff = true, tcsRate = 0, tdsRate = 0 } = options;
  if(options.canonical){
    const amounts=invoiceAmounts(lines.map(l=>({quantity:String(l.quantity),rate:String(l.unitPrice),taxRate:String(l.gstRate),taxTreatment:l.taxTreatment==='taxable'?'Regular':l.taxTreatment==='reverse-charge'?'RCM':l.taxTreatment==='exempt'?'Exempt':'Out of scope',taxInclusive:l.taxInclusive,discount:l.priceDiscount,lineKind:l.lineKind})),rcm,options.adjustments);
    const computed:LineTax[]=lines.map((l,i)=>{const a=amounts.lines[i],tax=Number(a.tax),base=Number(a.base),cgst=supplyType==='intra-state'?round2(tax/2):0,sgst=supplyType==='intra-state'?round2(tax-cgst):0;
      return {lineId:l.id,grossValue:round2(base+Number(a.discount)+Number(a.documentDiscount)),discount:Number(a.discount),documentDiscountShare:Number(a.documentDiscount),chargeShare:0,taxableValue:base,gstRate:l.gstRate,cgst,sgst,igst:supplyType==='intra-state'?0:tax,cess:0,totalTax:tax,lineTotal:round2(base+tax),treatment:l.taxTreatment};});
    const total=Number(amounts.total);
    return {lines:computed,taxableValue:Number(amounts.subtotal),nonTaxableCharges:0,documentDiscount:Number(amounts.documentDiscount),cgst:round2(computed.reduce((s,l)=>s+l.cgst,0)),sgst:round2(computed.reduce((s,l)=>s+l.sgst,0)),igst:round2(computed.reduce((s,l)=>s+l.igst,0)),cess:0,totalTax:Number(amounts.tax),grossTotal:Number(amounts.beforeRounding),roundOff:Number(amounts.rounding),total,tcs:0,tdsWithheld:0,netReceivable:total,supplyType,rcm};
  }


  const netValues = lines.map(lineNetValue);
  const discountShares = apportion(round2(documentDiscount), netValues);

  const taxableCharges = charges.filter((charge) => charge.taxable);
  const nonTaxableCharges = round2(
    charges.filter((charge) => !charge.taxable).reduce((sum, charge) => sum + charge.amount, 0)
  );
  const taxableChargeTotal = round2(taxableCharges.reduce((sum, charge) => sum + charge.amount, 0));
  const chargeShares = apportion(taxableChargeTotal, netValues);

  const lineTaxes: LineTax[] = lines.map((line, index) => {
    const gross = lineGross(line);
    const discount = lineDiscount(line);
    const documentDiscountShare = discountShares[index];
    const chargeShare = chargeShares[index];
    const taxableValue = round2(gross - discount - documentDiscountShare + chargeShare);

    // Reverse charge and the non-taxable treatments produce no output tax, but
    // the taxable value is still reported in the return.
    const taxable = attractsTax(line.taxTreatment) && !rcm;
    const rate = taxable ? line.gstRate : 0;

    let cgst = 0;
    let sgst = 0;
    let igst = 0;
    if (taxable && rate > 0) {
      if (supplyType === "intra-state") {
        cgst = round2((taxableValue * rate) / 200);
        sgst = round2((taxableValue * rate) / 200);
      } else {
        // Exports under LUT are zero-rated; without LUT they carry IGST. The
        // treatment on the line decides, which is why "zero-rated" is explicit.
        igst = round2((taxableValue * rate) / 100);
      }
    }

    if(line.taxInclusive&&taxable&&!discount&&!documentDiscountShare&&!chargeShare){
      const total=round2(line.quantity*line.unitPrice-taxableValue);
      if(supplyType==='intra-state'){cgst=round2(total/2);sgst=round2(total-cgst);}else igst=total;
    }

    const cessPercent = taxable && line.cessRate ? round2((taxableValue * line.cessRate) / 100) : 0;
    const cessUnit = taxable && line.cessPerUnit ? round2(line.quantity * line.cessPerUnit) : 0;
    const cess = round2(cessPercent + cessUnit);

    const totalTax = round2(cgst + sgst + igst + cess);
    return {
      lineId: line.id,
      grossValue: gross,
      discount,
      documentDiscountShare,
      chargeShare,
      taxableValue,
      gstRate: rate,
      cgst,
      sgst,
      igst,
      cess,
      totalTax,
      lineTotal: round2(taxableValue + totalTax),
      treatment: line.taxTreatment
    };
  });

  const taxableValue = round2(lineTaxes.reduce((sum, line) => sum + line.taxableValue, 0));
  const cgst = round2(lineTaxes.reduce((sum, line) => sum + line.cgst, 0));
  const sgst = round2(lineTaxes.reduce((sum, line) => sum + line.sgst, 0));
  const igst = round2(lineTaxes.reduce((sum, line) => sum + line.igst, 0));
  const cess = round2(lineTaxes.reduce((sum, line) => sum + line.cess, 0));
  const totalTax = round2(cgst + sgst + igst + cess);

  const grossTotal = round2(taxableValue + totalTax + nonTaxableCharges);
  const rounded = roundOff ? Math.round(grossTotal) : grossTotal;
  const roundOffAmount = round2(rounded - grossTotal);

  // TCS under 206C(1H) is collected on the sale consideration including GST.
  const tcs = tcsRate > 0 ? round2((rounded * tcsRate) / 100) : 0;
  const total = round2(rounded + tcs);

  // The customer deducts TDS on the taxable value, not on the GST component —
  // GST is excluded from TDS where it is shown separately on the invoice.
  const tdsWithheld = tdsRate > 0 ? round2((taxableValue * tdsRate) / 100) : 0;

  return {
    lines: lineTaxes,
    taxableValue,
    nonTaxableCharges,
    documentDiscount: round2(documentDiscount),
    cgst,
    sgst,
    igst,
    cess,
    totalTax,
    grossTotal,
    roundOff: roundOffAmount,
    total,
    tcs,
    tdsWithheld,
    netReceivable: round2(total - tdsWithheld),
    supplyType,
    rcm
  };
};

/**
 * TCS applies only once the buyer's aggregate consideration crosses the
 * threshold in a financial year, and then only on the excess. Section 206C(1H)
 * also stands down entirely where the buyer is deducting TDS under 194Q.
 */
export const tcsApplicable = (
  config: Pick<SalesConfig, "tcsEnabled" | "tcsThreshold">,
  turnoverWithBuyerSoFar: number,
  thisInvoiceValue: number,
  buyerDeductsTds194Q = false
): { applicable: boolean; amountLiable: number; reason: string } => {
  if (!config.tcsEnabled) {
    return { applicable: false, amountLiable: 0, reason: "TCS is not enabled for this entity." };
  }
  if (buyerDeductsTds194Q) {
    return { applicable: false, amountLiable: 0, reason: "Buyer deducts TDS under section 194Q, so TCS under 206C(1H) does not apply." };
  }
  const after = turnoverWithBuyerSoFar + thisInvoiceValue;
  if (after <= config.tcsThreshold) {
    return { applicable: false, amountLiable: 0, reason: `Aggregate consideration is within the ${config.tcsThreshold.toLocaleString("en-IN")} threshold.` };
  }
  const amountLiable = round2(after - Math.max(turnoverWithBuyerSoFar, config.tcsThreshold));
  return { applicable: true, amountLiable, reason: "Aggregate consideration exceeds the threshold; TCS applies on the excess." };
};

/** Rate-wise summary — the shape GSTR-1 and the invoice footer both need. */
export interface RateSummaryRow {
  gstRate: number;
  taxableValue: number;
  cgst: number;
  sgst: number;
  igst: number;
  cess: number;
}

export const rateWiseSummary = (tax: DocumentTax): RateSummaryRow[] => {
  const byRate = new Map<number, RateSummaryRow>();
  for (const line of tax.lines) {
    const row = byRate.get(line.gstRate) ?? { gstRate: line.gstRate, taxableValue: 0, cgst: 0, sgst: 0, igst: 0, cess: 0 };
    row.taxableValue = round2(row.taxableValue + line.taxableValue);
    row.cgst = round2(row.cgst + line.cgst);
    row.sgst = round2(row.sgst + line.sgst);
    row.igst = round2(row.igst + line.igst);
    row.cess = round2(row.cess + line.cess);
    byRate.set(line.gstRate, row);
  }
  return [...byRate.values()].sort((left, right) => left.gstRate - right.gstRate);
};

/** HSN summary for GSTR-1 Table 12. */
export interface HsnSummaryRow {
  hsnSac: string;
  description: string;
  uqc: string;
  quantity: number;
  taxableValue: number;
  cgst: number;
  sgst: number;
  igst: number;
  cess: number;
}

export const hsnSummary = (lines: SalesLine[], tax: DocumentTax): HsnSummaryRow[] => {
  const byHsn = new Map<string, HsnSummaryRow>();
  const taxByLine = new Map(tax.lines.map((line) => [line.lineId, line]));
  for (const line of lines) {
    const key = line.hsnSac ?? "UNSPECIFIED";
    const computed = taxByLine.get(line.id);
    if (!computed) continue;
    const row = byHsn.get(key) ?? {
      hsnSac: key,
      description: line.description,
      uqc: line.uqc ?? "NOS",
      quantity: 0,
      taxableValue: 0,
      cgst: 0,
      sgst: 0,
      igst: 0,
      cess: 0
    };
    row.quantity = round2(row.quantity + line.quantity);
    row.taxableValue = round2(row.taxableValue + computed.taxableValue);
    row.cgst = round2(row.cgst + computed.cgst);
    row.sgst = round2(row.sgst + computed.sgst);
    row.igst = round2(row.igst + computed.igst);
    row.cess = round2(row.cess + computed.cess);
    byHsn.set(key, row);
  }
  return [...byHsn.values()];
};

/** Indian numbering words for the "amount in words" line on the invoice. */
const ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
  "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

const twoDigits = (value: number): string =>
  value < 20 ? ONES[value] : `${TENS[Math.floor(value / 10)]}${value % 10 ? ` ${ONES[value % 10]}` : ""}`;

const threeDigits = (value: number): string => {
  const hundreds = Math.floor(value / 100);
  const rest = value % 100;
  return [hundreds ? `${ONES[hundreds]} Hundred` : "", rest ? twoDigits(rest) : ""].filter(Boolean).join(" ");
};

/** Converts to the lakh/crore grouping used on Indian invoices. */
export const amountInWords = (value: number): string => {
  const rounded = round2(Math.abs(value));
  const rupees = Math.floor(rounded);
  const paise = Math.round((rounded - rupees) * 100);
  if (rupees === 0 && paise === 0) return "Rupees Zero Only";

  const crore = Math.floor(rupees / 10_000_000);
  const lakh = Math.floor((rupees % 10_000_000) / 100_000);
  const thousand = Math.floor((rupees % 100_000) / 1000);
  const remainder = rupees % 1000;

  const parts = [
    crore ? `${threeDigits(crore)} Crore` : "",
    lakh ? `${twoDigits(lakh)} Lakh` : "",
    thousand ? `${twoDigits(thousand)} Thousand` : "",
    remainder ? threeDigits(remainder) : ""
  ].filter(Boolean);

  const sign = value < 0 ? "Minus " : "";
  const rupeeWords = parts.length ? parts.join(" ") : "Zero";
  const paiseWords = paise ? ` and ${twoDigits(paise)} Paise` : "";
  return `${sign}Rupees ${rupeeWords}${paiseWords} Only`;
};

